import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../../services/apiConfig';
import { useSiteSettings } from '../../context/SiteSettingsContext';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { settings } = useSiteSettings();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/accounts/password-reset/`, { email });
      setSent(true);
      toast.success('If that email is registered, a reset link has been sent.');
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        'Could not submit the request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledWrapper>
      <div className="container">
        <div className="card">
          <img src={settings?.header_logo || '/APPSN.jpg'} alt="APPSN" className="logo" />
          <span className="title">Forgot Password</span>
          <p className="subtitle">APPSN - FCT Branch</p>

          {sent ? (
            <div className="success-box">
              <p>
                If an account exists for <strong>{email}</strong>, we've emailed
                a password reset link to it. Check your inbox (and spam folder).
              </p>
              <p className="hint">The link is valid for 15 minutes.</p>
              <Link to="/login" className="back-link">Back to sign in</Link>
            </div>
          ) : (
            <>
              <p className="hint">
                Enter the email address linked to your APPSN account and we'll
                send you a link to reset your password.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="inputBox">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <span>Email Address</span>
                </div>
                <button className="enter" type="submit" disabled={loading || !email}>
                  {loading ? 'Sending...' : 'Send reset link'}
                </button>
              </form>
              <p className="register-link">
                Remembered it?{' '}
                <Link to="/login">Back to sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  .container {
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    background: #F8FAFB;
  }
  .card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
    width: 380px;
    max-width: 95vw;
    background: #e3e3e3;
    box-shadow: 16px 16px 32px #c8c8c8, -16px -16px 32px #fefefe;
    border-radius: 12px;
    padding: 2.5rem 1.5rem;
  }
  .logo {
    height: 80px;
    object-fit: contain;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  .title {
    color: #0B5D1E;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: bold;
    font-size: x-large;
    font-family: 'Poppins', sans-serif;
  }
  .subtitle { color: #6b7280; font-size: 0.875rem; margin-top: -15px; }
  .hint     { color: #374151; font-size: 0.875rem; text-align: center; padding: 0 6px; margin: 0; }
  form { display: flex; flex-direction: column; align-items: center; gap: 22px; width: 100%; }
  .inputBox { position: relative; width: 280px; max-width: 100%; }
  .inputBox input {
    width: 100%; padding: 12px; outline: none; border: none; color: #000;
    font-size: 1em; background: transparent;
    border-left: 2px solid #0B5D1E; border-bottom: 2px solid #0B5D1E;
    transition: 0.1s; border-bottom-left-radius: 8px;
  }
  .inputBox span {
    margin-top: 5px; position: absolute; left: 0; transform: translateY(-4px);
    margin-left: 10px; padding: 10px; pointer-events: none; font-size: 12px;
    color: #374151; text-transform: uppercase; transition: 0.5s; letter-spacing: 3px;
    border-radius: 8px;
  }
  .inputBox input:valid ~ span, .inputBox input:focus ~ span {
    transform: translateX(113px) translateY(-15px); font-size: 0.8em;
    padding: 5px 10px; background: #0B5D1E; letter-spacing: 0.2em; color: #fff;
  }
  .inputBox input:valid, .inputBox input:focus {
    border: 2px solid #0B5D1E; border-radius: 8px;
  }
  .enter {
    height: 48px; width: 180px; border-radius: 8px; border: 2px solid #0B5D1E;
    cursor: pointer; background-color: transparent; transition: 0.3s;
    text-transform: uppercase; font-size: 0.8rem; font-weight: 600;
    letter-spacing: 2px; color: #0B5D1E;
  }
  .enter:hover { background-color: #0B5D1E; color: white; }
  .enter:disabled { opacity: 0.5; cursor: not-allowed; }
  .register-link { font-size: 0.875rem; color: #6b7280; margin: 0; }
  .register-link a, .back-link {
    color: #0B5D1E; font-weight: 600; text-decoration: none;
  }
  .register-link a:hover, .back-link:hover { text-decoration: underline; }
  .success-box {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    text-align: center; padding: 8px 4px;
  }
  .success-box p { margin: 0; font-size: 0.9rem; color: #374151; }
`;
